/**
 * Copyright (C) 2020 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import Terminal, { SteerableBeamPriority, InstallationType } from '../shared/entities/terminal';
import { valueOrPlaceholder, abbreviateNetworkName, EMPTY_FIELD_STRING_PLACEHOLDER } from './StringUtils';
import { capitalize } from 'lodash';
import { getLastReportTimeOrDuration } from '../components/fleet/TailDetailsPanel/TabbedPanel/OfflineTerminalDetailsLastReportedBanner';
import { getSiteIdText, getTxPowerDbText } from './TerminalUtilities';

export const isListItemHidden = (terminal: Terminal, otherDatapathUuids: string[]): boolean =>
  !!otherDatapathUuids.filter((hiddenId: string) =>
    [terminal.datapathUuid, ...(terminal.datapathUuidsOnAircraft || [])].includes(hiddenId),
  ).length;

export const isListItemSelected = (terminal: Terminal, selectedDatapathUuid: string): boolean =>
  !![terminal.datapathUuid, ...(terminal.datapathUuidsOnAircraft || [])].find(
    (datapathUuid: string) => datapathUuid === selectedDatapathUuid,
  );

export const getSteerableBeamPriorityText = (steerableBeamPriority: SteerableBeamPriority | undefined | null) => {
  if (steerableBeamPriority === SteerableBeamPriority.PRIMARY) {
    return 'PRI';
  }
  if (steerableBeamPriority === SteerableBeamPriority.SECONDARY) {
    return 'SEC';
  }
  return EMPTY_FIELD_STRING_PLACEHOLDER;
};

export const getBeamDisplayText = (terminal: Terminal): string => {
  if (terminal.connectivityStatus && terminal.connectivityStatus !== 'offline') {
    return capitalize(terminal.connectivityStatus);
  } else if (terminal.sourceTime) {
    return `Not Connected (${getLastReportTimeOrDuration(terminal.sourceTime)})`;
  } else {
    return `Last Connection ${EMPTY_FIELD_STRING_PLACEHOLDER}`;
  }
};

export const getNetworkAndBeamDisplayText = (terminal: Terminal): string => {
  if (terminal.connectivityStatus && terminal.connectivityStatus !== 'offline') {
    return `Net : ${valueOrPlaceholder(abbreviateNetworkName(terminal.networkOrigin))} / Beam : ${valueOrPlaceholder(
      terminal.beamDisplayName,
    )}`;
  } else if (terminal.beamDisplayName && terminal.networkOrigin) {
    return `Last Net : ${valueOrPlaceholder(
      abbreviateNetworkName(terminal.networkOrigin),
    )} / Last Beam : ${valueOrPlaceholder(terminal.beamDisplayName)}`;
  } else if (terminal.beamDisplayName && !terminal.networkOrigin) {
    return `Last Net : ${EMPTY_FIELD_STRING_PLACEHOLDER} / Last Beam : ${valueOrPlaceholder(terminal.beamDisplayName)}`;
  } else if (!terminal.beamDisplayName && terminal.networkOrigin) {
    return `Last Net : ${valueOrPlaceholder(
      abbreviateNetworkName(terminal.networkOrigin),
    )} / Last Beam : ${EMPTY_FIELD_STRING_PLACEHOLDER}`;
  } else {
    return `Last Net : ${EMPTY_FIELD_STRING_PLACEHOLDER} / Last Beam : ${EMPTY_FIELD_STRING_PLACEHOLDER}`;
  }
};

export const getTxPowerAndSiteIdText = (terminal: Terminal): string => {
  if (terminal.connectivityStatus && terminal.connectivityStatus !== 'offline') {
    return `${getTxPowerDbText(terminal.lwTxPowerDb)} / ${getSiteIdText(terminal.lwSiteId)}`;
  } else {
    return `Last ${getTxPowerDbText(terminal.lwTxPowerDb)} / ${getSiteIdText(terminal.lwSiteId)}`;
  }
};

export const convertTerminalListIndexToTerminalTabIndex = (
  selectedTerminalListSubRowIndex: number | undefined,
  installationType: InstallationType | undefined,
) => {
  switch (installationType) {
    case InstallationType.KU_PLUS_KA:
      return selectedTerminalListSubRowIndex || 0;
    case InstallationType.DUAL_TERMINAL:
      return (selectedTerminalListSubRowIndex || 0) < 2 ? 0 : 1;
    default:
      return undefined;
  }
};
